// Weiche fuer die UX-Vorschlaege (Web P1–P7). Modell wie juunitDocs:
// EIN Hauptschalter pro Benutzer („Neues Design", `ui_design`, siehe
// lib/theme.ts → isNewUx) plus ein Server-Flag pro Vorschlag mit drei Stufen:
//   off      = fuer niemanden,
//   vorschau = nur fuer Benutzer mit eingeschaltetem Hauptschalter,
//   an       = fuer alle (auch classic).
// Die aufgeloesten Stufen liefert GET /api/me als `ux_flags`. Fehlt das ganze
// Feld (alter Server), ist alles „off"; fehlt ein einzelner Schluessel, gilt
// „vorschau"; ein unbekannter Wert gilt als „off".

import { signal, type Signal } from "@preact/signals";
import {
  UX_FLAG_DEFAULT_MODE,
  UX_FLAG_MODES,
  resolveUxFlagMode,
  uxFlagActive,
  type UxFlagMode,
  type UxFlagName,
} from "@juuplan/shared";
import { isNewUx } from "./theme";

export type UxFlagLevel = UxFlagMode;

/** Nur die Web-Flags; iOS-Flags sind fuer diesen Client bedeutungslos. */
export type UxWebFlag = Extract<UxFlagName, `ux_web_${string}`>;

export const DEFAULT_UX_LEVEL: UxFlagLevel = UX_FLAG_DEFAULT_MODE;

export function isUxFlagLevel(v: unknown): v is UxFlagLevel {
  return (UX_FLAG_MODES as readonly unknown[]).includes(v);
}

/**
 * Flag-Stufen aus /api/me (Regel des Leiters, 27.10.):
 *   - `null`  = /api/me kam OHNE `ux_flags` (alter Server ohne die neuen
 *               Routen) → ALLE Flags „off".
 *   - Objekt  = Feld vorhanden; fehlt darin ein Schluessel → „vorschau";
 *               ein unbekannter Wert wurde schon beim Einlesen zu „off".
 * Startwert `{}` (noch nicht geladen): Standard „vorschau", passend zum
 * Design aus dem lokalen Cache — sonst flackert das neue Design beim Laden.
 */
export const uxFlagsSignal: Signal<Record<string, UxFlagLevel> | null> = signal({});

/** Rohwert aus /api/me in die Signal-Form bringen (rein, testbar). */
export function parseUxFlags(raw: unknown): Record<string, UxFlagLevel> | null {
  if (!raw || typeof raw !== "object") return null;
  const next: Record<string, UxFlagLevel> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    next[k] = isUxFlagLevel(v) ? v : "off";
  }
  return next;
}

/** Aus GET /api/me uebernehmen. Abmelden (`null`) = zurueck auf „nicht geladen". */
export function syncUxFlagsFromServer(user: { ux_flags?: unknown } | null | undefined): void {
  // Kein Benutzer (abgemeldet, oder kaputte Antwort) = „nicht geladen", nie ein Absturz.
  uxFlagsSignal.value = user == null ? {} : parseUxFlags(user.ux_flags);
}

/** Stufe eines Flags nach obiger Regel (rein, testbar). */
export function resolveFlagLevel(
  flags: Record<string, UxFlagLevel> | null,
  flag: UxWebFlag,
): UxFlagLevel {
  // Eine Quelle fuer die Regel: @juuplan/shared (null → off, Schluessel fehlt
  // → vorschau, unbekannter Wert → off).
  return resolveUxFlagMode(flags, flag);
}

export function uxFlagLevel(flag: UxWebFlag): UxFlagLevel {
  return resolveFlagLevel(uxFlagsSignal.value, flag);
}

/** Reine Entscheidungsregel (testbar ohne Signale). */
export function resolveUx(level: UxFlagLevel, newUx: boolean): boolean {
  return uxFlagActive(level, newUx ? "juunit" : "classic");
}

/**
 * Ist der Vorschlag fuer den aktuellen Benutzer aktiv? Reaktiv (liest
 * Signale) — in Komponenten direkt aufrufen, Umschalten rendert neu.
 */
export function uxEnabled(flag: UxWebFlag): boolean {
  return resolveUx(uxFlagLevel(flag), isNewUx());
}
